import { icons } from "antd/es/image/PreviewGroup";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { saveAs } from "file-saver";

export const exportRadarDataAsCSV = (data, normalize = true) => {
  const rows = [];
  const indicators = Array.from(
    new Set(data.flatMap((d) => d.indicators.map((i) => i.name)))
  );
  rows.push(["Designation", ...indicators]);
  data.forEach((d) => {
    const row = [d.designation];
    indicators.forEach((i) => {
      const val = d.indicators.find((x) => x.name === i)?.value || 0;
      row.push(normalize ? (val / Math.max(...data.map(d => d.indicators.find(x => x.name === i)?.value || 1))).toFixed(3) : val);
    });
    rows.push(row);
  });
  const csvContent = rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, "radar_data_export.csv");
};


/**
 * Load an image URL to a data URL (safe for CORS).
 */
const loadImageAsDataURL = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = src;
    });

/**
 * Render a DOM node (or an ECharts instance's DOM) to canvas via html2canvas.
 * If echartsInstance is provided, we temporarily resize and tweak legend/grid
 * to produce a clean export, then restore afterward.
 */
async function renderToCanvas({ targetEl, echartsInstance }) {
  let originalOption, originalSize;
  if (echartsInstance) {
    const dom = echartsInstance.getDom();
    originalOption = echartsInstance.getOption?.();
    originalSize = { width: dom.style.width, height: dom.style.height };

    // Export size tuned for A4-landscape clarity
    dom.style.width = "1123px";
    dom.style.height = "620px";
    echartsInstance.resize({ width: 1123, height: 620 });

    // Simplify legend layout to avoid overlap
    echartsInstance.setOption(
        {
          legend: {
            orient: "horizontal",
            type: "plain",
            bottom: 0,
            icon: "circle",
            left: "center",
          },
          grid: { bottom: 80 },
        },
        false
    );

    // Let ECharts paint at the new size
    await new Promise((r) => setTimeout(r, 250));
  }

  const canvas = await html2canvas(targetEl, {
    backgroundColor: "#fff",
    scale: window.devicePixelRatio > 1 ? 2 : 1.5, // sharper on HiDPI
    useCORS: true,
    allowTaint: true,
    // For charts, we use explicit size; for tables we let it use natural size
    width: echartsInstance ? 1123 : undefined,
    height: echartsInstance ? 620 : undefined,
    onclone: (clonedDoc) => {
      // prevent sticky headers/ant table shadow from occluding rows
      clonedDoc.querySelectorAll(".ant-table-header").forEach((el) => {
        el.style.boxShadow = "none";
      });
    },
  });

  // restore ECharts
  if (echartsInstance) {
    const dom = echartsInstance.getDom();
    dom.style.width = originalSize?.width;
    dom.style.height = originalSize?.height;
    echartsInstance.resize();
    echartsInstance.setOption(originalOption || {}, true);
  }

  return canvas;
}

/**
 * Draw a header band and return y after the header.
 */
function drawHeader(pdf, { themeColor, title }) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  pdf.setFillColor(themeColor);
  pdf.rect(0, 0, pageWidth, 20, "F");
  pdf.setTextColor("#fff");
  pdf.setFontSize(14);
  pdf.text(title, pageWidth / 2, 14, { align: "center" });
  return 22;
}

/**
 * Draw footnote + logos along the bottom of the page.
 */
async function drawFooter(pdf, { footnote, logos = [] }) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  if (footnote) {
    pdf.setFontSize(10);
    pdf.setTextColor("#555");
    pdf.text(String(footnote), 10, pageHeight - 18, { maxWidth: pageWidth - 20 });
  }

  if (logos.length) {
    const logoSize = 18;
    const gap = 8;
    const totalWidth = logos.length * logoSize + (logos.length - 1) * gap;
    const startX = (pageWidth - totalWidth) / 2;
    const y = pageHeight - 22;

    for (let i = 0; i < logos.length; i++) {
      try {
        const src = logos[i]?.src ?? logos[i];
        const img = await loadImageAsDataURL(src);
        pdf.addImage(img, "PNG", startX + i * (logoSize + gap), y, logoSize, logoSize);
      } catch {
        // ignore failed logo
      }
    }
  }
}

/**
 * If canvas is taller than fits on a page, slice it and add extra pages.
 */
function addCanvasToPDFMultipage(pdf, canvas, startY) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // target image width inside margins
  const marginX = 10;
  const usableWidth = pageWidth - marginX * 2;
  const imgHeight = (canvas.height * usableWidth) / canvas.width;

  const headerSpace = startY; // already consumed by header
  const footerSpace = 28; // approx for footnote+logos
  const usableHeightFirstPage = pageHeight - headerSpace - footerSpace;
  const usableHeightOtherPages = pageHeight - 20 /*header*/ - footerSpace;

  if (imgHeight <= usableHeightFirstPage) {
    // fits on first page
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", marginX, startY, usableWidth, imgHeight);
    return;
  }

  // Draw first slice
  const pxPerMM = canvas.height / imgHeight; // conversion based on scaled draw
  const firstSliceHeightPx = Math.floor(usableHeightFirstPage * pxPerMM);

  // Helper to draw a slice at srcY of height slicePx on current page at (x,y) with height hMM
  const drawSlice = (srcY, slicePx, y, heightMM) => {
    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = slicePx;
    const ctx = sliceCanvas.getContext("2d");
    ctx.drawImage(canvas, 0, srcY, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
    pdf.addImage(sliceCanvas.toDataURL("image/png"), "PNG", marginX, y, usableWidth, heightMM);
  };

  // First page
  drawSlice(0, firstSliceHeightPx, startY, usableHeightFirstPage);

  // Remaining pages
  let drawnPx = firstSliceHeightPx;
  while (drawnPx < canvas.height) {
    pdf.addPage("a4", "landscape");
    const headerY = drawHeader(pdf, { themeColor: "#003366", title: "" }); // blank title on subsequent pages
    const remainingPx = canvas.height - drawnPx;
    const slicePx = Math.min(
        Math.floor(usableHeightOtherPages * pxPerMM),
        remainingPx
    );
    drawSlice(drawnPx, slicePx, headerY, usableHeightOtherPages);
    drawnPx += slicePx;
  }
}

/**
 * Public API: exportChartAsPDF
 * - Accepts ECharts instance, ReactECharts ref, or plain DOM node (tables, cards).
 * - Creates A4 landscape PDF with a header, captured content (multipage if needed),
 *   footnote, and centered logos. High-DPI friendly.
 */
export const exportChartAsPDF = async ({
                                         chartElement,         // ECharts instance OR ReactECharts ref.current OR DOM node
                                         filename,
                                         footnote,
                                         logos = [],
                                         title = "Malawi Eyecare Training and Guidelines",
                                         themeColor = "#003366",
                                       }) => {
  if (!chartElement) return;

  // Normalize input
  let echartsInstance = null;
  let captureEl = null;

  if (chartElement?.getEchartsInstance) {
    echartsInstance = chartElement.getEchartsInstance();
    captureEl = echartsInstance.getDom();
  } else if (chartElement?.getDom && chartElement?.setOption) {
    echartsInstance = chartElement;
    captureEl = echartsInstance.getDom();
  } else if (chartElement instanceof HTMLElement) {
    captureEl = chartElement; // table/card DOM
  } else {
    throw new Error("exportChartAsPDF: chartElement must be an ECharts instance or a DOM node");
  }

  // Render to canvas
  const canvas = await renderToCanvas({ targetEl: captureEl, echartsInstance });

  // Build PDF
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const startY = drawHeader(pdf, { themeColor, title });

  // Add canvas (multipage if needed)
  addCanvasToPDFMultipage(pdf, canvas, startY);

  // Footer (footnote + logos) on last page
  await drawFooter(pdf, { footnote, logos });

  // Save
  pdf.save(String(filename || "export.pdf"));
};

/**
 * Optional: direct image export from ECharts.
 */
export const exportChartAsImage = async (chartRef, type, fileName) => {
  const canvas = chartRef.current?.getEchartsInstance().getRenderedCanvas();
  canvas?.toBlob((blob) => {
    if (blob) {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(a.href);
    }
  }, `image/${type}`);
};
